import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import {
  firebaseConfigured,
  requestSignInLink,
} from "@/lib/firebase";

/** localStorage key shared with AuthFinishPage so the magic-link tab can
 *  recover the user's intended target after a cross-tab journey. */
const SIGNIN_NEXT_KEY = "cura-studio:signin-next";

/** Only honour same-origin absolute paths. Reject `//evil.example` and
 *  anything that smells like an open redirect. */
function safeNext(raw: string | null): string | null {
  if (!raw) return null;
  if (!raw.startsWith("/") || raw.startsWith("//")) return null;
  return raw;
}

/**
 * Email-link sign-in. The user enters their email, we trigger Firebase
 * to email them a magic link, they click it, the app catches them at
 * /auth/finish, exchanges the link, and lands them logged in.
 *
 * Falls through to a dev-mode persona switcher if Firebase isn't
 * configured yet (apiKey not set) — useful during the period where the
 * Firebase console "Get started" click hasn't happened.
 */
export function SignInPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();

  // Resume-after-signin: stash the `next` path in localStorage so that the
  // magic-link tab (a fresh window started from the user's email client)
  // can recover it. URL query params don't survive the round-trip — the
  // email link is on Firebase's domain.
  useEffect(() => {
    const next = safeNext(searchParams.get("next"));
    if (next) {
      try {
        window.localStorage.setItem(SIGNIN_NEXT_KEY, next);
      } catch {
        /* storage full / disabled — degrade silently */
      }
    }
  }, [searchParams]);

  if (!firebaseConfigured()) {
    return <DevPersonaSwitcher />;
  }

  return (
    <div className="mx-auto max-w-md px-6 py-16 text-center">
      <img src="/cura-logo.svg" alt="+CURA" className="mx-auto mb-6 h-8" />
      <h1 className="m-0 mb-2 text-xl font-semibold tracking-tight text-fg-1">
        Sign in to Studio
      </h1>
      <p className="m-0 mb-8 text-fg-3">
        Enter your work email. We'll send you a one-tap sign-in link.
      </p>

      {sent ? (
        <div className="rounded-md border-l-[3px] border-[var(--ok)] bg-[#F1F9F4] p-4 text-left">
          <h3 className="m-0 mb-1 text-sm font-semibold">Check your inbox</h3>
          <p className="m-0 text-sm text-fg-2">
            We sent a sign-in link to <strong>{email}</strong>. Click it on this
            device — the link is single-use and expires in 1 hour.
          </p>
        </div>
      ) : (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setError(null);
            setBusy(true);
            try {
              await requestSignInLink(email);
              setSent(true);
            } catch (err) {
              setError((err as Error).message);
            } finally {
              setBusy(false);
            }
          }}
          className="flex flex-col gap-3"
        >
          <input
            type="email"
            required
            placeholder="you@yourpractice.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="field-input"
          />
          <button
            type="submit"
            disabled={busy || !email}
            className="rounded-md px-4 py-3 text-sm font-semibold text-white shadow-sm disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)]"
            style={{ background: "var(--studio)" }}
          >
            {busy ? "Sending…" : "Send sign-in link"}
          </button>
          {error && (
            <div className="rounded border-l-[3px] border-[var(--danger)] bg-[#FFF8F7] p-3 text-left text-sm text-[color:var(--danger-fg)]">
              {error}
            </div>
          )}
        </form>
      )}
    </div>
  );
}

/* ── Dev fallback: pick a persona without a real Firebase login ──────── */

const PERSONAS = [
  { email: "a.demo@northshore.example.com", role: "Practice manager · Northshore" },
  { email: "s.demo@northshore.example.com", role: "Approver · Northshore" },
  { email: "j.demo@riverside.example.com",  role: "Practice manager · Riverside" },
  { email: "evolve@thisisrdi.com",           role: "Studio admin · RDi (cross-tenant)" },
];

function DevPersonaSwitcher() {
  const [searchParams] = useSearchParams();
  // Resume-after-signin works in dev too: drop the user back on the path
  // they were trying to reach, not the landing page.
  const next = safeNext(searchParams.get("next"));
  return (
    <div className="mx-auto max-w-md px-6 py-16">
      <img src="/cura-logo.svg" alt="+CURA" className="mx-auto mb-6 h-8" />
      <h1 className="m-0 mb-2 text-center text-xl font-semibold tracking-tight text-fg-1">
        Choose a persona
      </h1>
      <p className="m-0 mb-6 text-center text-fg-3">
        Firebase Auth isn't configured yet — pick a seeded user to continue.
      </p>
      <ul data-chrome className="flex flex-col gap-2">
        {PERSONAS.map((p) => (
          <li key={p.email}>
            <button
              type="button"
              onClick={() => {
                window.localStorage.setItem("cura-studio:dev-user", p.email);
                window.location.assign(next ?? "/");
              }}
              className="w-full rounded-md border border-[var(--border-1)] bg-white px-4 py-3 text-left hover:border-[var(--studio)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)]"
            >
              <div className="text-sm font-semibold">{p.email}</div>
              <div className="text-xs text-fg-3">{p.role}</div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
